/*
 * promo-display.js
 * ---------------------------------------------------------------------------
 * Display-only promotional pricing for the storefront.
 *
 * IMPORTANT: This file ONLY changes what shoppers SEE. It does NOT create,
 * apply, or validate any Shopify discount. The real discount must be created
 * separately in Shopify Admin -> Discounts. Any scheduling here is display
 * gating only and does not affect what the cart actually charges.
 *
 * The authoritative on/off + schedule gate is server-rendered in
 * snippets/promo-display-config.liquid (window.promoDisplay.active). The
 * optional client-side window re-check below depends on the visitor's device
 * clock and is therefore best-effort only.
 *
 * DISPLAY MODE: the promo message REPLACES the price text in place, so it
 * inherits the theme's own price styling (font/size/colour) and the shopper
 * only ever sees one price. The original/compare-at price is left untouched.
 *
 * All DOM work is wrapped defensively: if the theme's markup/selectors differ,
 * the script fails silently rather than breaking the page.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var config = window.promoDisplay;
  if (!config) return;

  /* ---- Theme selectors (edit here if your theme markup changes) ---- */
  var SELECTORS = {
    cardWrapper: '.cl-product-card-inner-container',
    cardPrice: '.price-variation',
    productPagePrice: '.product-details-price-snippet .sale-price',
    productFormPrice: '.product-form-price .price'
  };

  // Marks an element whose text we have replaced, so we never double-discount.
  var APPLIED_ATTR = 'data-promo-applied';

  /* ---- Optional client-side window re-check (display only, browser time) ---- */
  function parseLocalDateTime(str) {
    // Expects "YYYY-MM-DD HH:mm". Returns ms timestamp or null.
    var m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(str).trim());
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0).getTime();
  }

  function withinClientWindow() {
    // The authoritative gate is config.active (server-rendered). This only
    // guards against showing a promo on a long-cached page after it has ended.
    try {
      var now = Date.now();
      if (config.startDatetime) {
        var start = parseLocalDateTime(config.startDatetime);
        if (start && now < start) return false;
      }
      if (config.endDatetime) {
        var end = parseLocalDateTime(config.endDatetime);
        if (end && now > end) return false;
      }
    } catch (e) { /* fail open: defer to the server-side gate */ }
    return true;
  }

  if (!config.active) return;          // server says inactive -> stop
  if (!withinClientWindow()) return;   // best-effort client guard

  /* ---- Price helpers ---- */
  function parsePrice(text) {
    if (text == null) return NaN;
    // Pull the first number, ignoring currency symbols, spaces and thousands commas.
    var cleaned = String(text).replace(/[^0-9.,]/g, ' ').trim();
    var match = cleaned.match(/[0-9][0-9,]*(?:\.[0-9]+)?/);
    if (!match) return NaN;
    return parseFloat(match[0].replace(/,/g, ''));
  }

  function applyPercentage(price, pct) {
    var p = parseFloat(pct) || 0;
    return Math.max(0, price - (price * (p / 100)));
  }

  function applyFixed(price, amount) {
    var a = parseFloat(amount) || 0;
    return Math.max(0, price - a);
  }

  function formatGBP(value) {
    var v = Math.max(0, Number(value) || 0); // never below GBP 0.00
    var parts = v.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); // thousands separators
    return '\u00A3' + parts[0] + '.' + parts[1];               // pound sign
  }

  function computePromoPrice(originalPrice) {
    if (config.discountType === 'fixed_amount') {
      return applyFixed(originalPrice, config.discountValue);
    }
    return applyPercentage(originalPrice, config.discountValue);
  }

  function buildMessage(formattedPrice) {
    var fmt = config.messageFormat || '{price} WITH CODE {code}';
    return fmt
      .replace(/\{price\}/g, formattedPrice)
      .replace(/\{code\}/g, config.discountCode || '')
      .replace(/\{discount\}/g, config.discountValue != null ? config.discountValue : '');
  }

  /* ---- Eligibility (central decision function) ---- */
  function inList(handle, list) {
    if (!handle || !list || !list.length) return false;
    return list.indexOf(handle) !== -1;
  }

  function isEligible(handle, collections) {
    // Excluded products ALWAYS win.
    if (inList(handle, config.excludedProducts)) return false;

    switch (config.targetType) {
      case 'all_products':
        return true;
      case 'selected_products':
        return inList(handle, config.selectedProducts);
      case 'collection':
        if (!config.targetCollection) return false;
        return !!collections && collections.indexOf(config.targetCollection) !== -1;
      default:
        return false;
    }
  }

  /* ---- Context helpers ---- */
  function getUrlCollectionHandle() {
    var m = /\/collections\/([^\/?#]+)/.exec(window.location.pathname);
    if (!m) return null;
    var handle = m[1];
    if (handle === 'all') return null; // ignore the "all" virtual collection
    return handle;
  }

  function getCardHandle(card) {
    var attr = card.getAttribute('data-product-handle');
    if (attr) return attr;
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = /\/products\/([^\/?#]+)/.exec(link.getAttribute('href') || '');
    return m ? m[1] : null;
  }

  function getCardCollections(card) {
    var attr = card.getAttribute('data-product-collections');
    if (attr) {
      try {
        var parsed = JSON.parse(attr);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return attr.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      }
    }
    // Fallback: on a collection page, every visible card belongs to that collection.
    var urlHandle = getUrlCollectionHandle();
    return urlHandle ? [urlHandle] : [];
  }

  /* ---- Render: REPLACE the price text in place ----
   * The promo message overwrites the price element's text so it inherits the
   * theme's own price styling. We store what we wrote in data-promo-applied:
   *  - if the element's current text equals what we last wrote, it's our own
   *    output -> skip (this is what stops a variant change from discounting the
   *    already-discounted figure again);
   *  - otherwise the theme has written a fresh price -> recompute from it.
   * `fallbackText` is the Liquid-rendered variant price, used only if the live
   * text can't be parsed (e.g. empty on first paint).
   */
  function applyPromo(el, fallbackText) {
    if (!el) return;
    var current = (el.textContent || '').trim();
    if (el.getAttribute(APPLIED_ATTR) === current) return; // our own output, leave it

    var base = current;
    if (isNaN(parsePrice(base)) && fallbackText) base = fallbackText;

    var original = parsePrice(base);
    if (isNaN(original)) return;

    var message = buildMessage(formatGBP(computePromoPrice(original)));
    el.textContent = message;
    el.setAttribute(APPLIED_ATTR, message);
  }

  /* ---- Product cards (collection / featured collection / search results) ---- */
  function processCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll(SELECTORS.cardWrapper);
    if (!cards || !cards.length) return; // fail silently if markup differs
    Array.prototype.forEach.call(cards, function (card) {
      try {
        var handle = getCardHandle(card);
        if (!handle) return;
        if (!isEligible(handle, getCardCollections(card))) return;
        var priceEl = card.querySelector(SELECTORS.cardPrice);
        if (!priceEl) return;
        applyPromo(priceEl);
      } catch (e) { /* fail silently per card */ }
    });
  }

  /* ---- Product page ---- */
  // Every price element we should decorate (main price + add-to-cart price).
  function getProductPriceElements() {
    var els = [];
    var a = document.querySelector(SELECTORS.productPagePrice);
    var b = document.querySelector(SELECTORS.productFormPrice);
    if (a) els.push(a);
    if (b) els.push(b);
    return els;
  }

  function processProductPage() {
    var data = window.currentProductPromoData;
    if (!data || !data.handle) return; // not a product page, or snippet missing
    if (!isEligible(data.handle, data.collections)) return;
    getProductPriceElements().forEach(function (el) {
      applyPromo(el, data.price);
    });
  }

  /* ---- Variant change handling (product page) ----
   * Themes rewrite the price text in place when the variant changes. We re-read
   * it and re-apply. We listen for common theme events AND watch each price
   * container as a fallback. If your theme fires a custom event name, add it to
   * the list below.
   */
  function bindVariantChanges() {
    var data = window.currentProductPromoData;
    if (!data || !isEligible(data.handle, data.collections)) return;
    if (!getProductPriceElements().length) return;

    var observers = [];

    function observeAll() {
      if (!('MutationObserver' in window)) return;
      observers = [];
      getProductPriceElements().forEach(function (el) {
        var target = el.parentNode || el;
        var obs = new MutationObserver(function () { refreshAll(); });
        obs.observe(target, { childList: true, subtree: true, characterData: true });
        observers.push(obs);
      });
    }

    function refreshAll() {
      window.requestAnimationFrame(function () {
        observers.forEach(function (o) { o.disconnect(); }); // don't observe our own write
        getProductPriceElements().forEach(function (el) {
          try { applyPromo(el); } catch (e) {}
        });
        observeAll();
      });
    }

    // 1) Common theme events (add your theme's own event name here if different).
    ['variant:change', 'variant:changed', 'variantChange', 'product:variant-change']
      .forEach(function (ev) { document.addEventListener(ev, refreshAll); });

    // 2) Fallback: watch each price element for theme-driven text changes.
    observeAll();

    // 3) If your theme exposes the selected variant id on a form input, you can
    //    also hook in here:
    // var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    // if (idInput) idInput.addEventListener('change', refreshAll);
  }

  /* ---- Init ---- */
  function init() {
    try { processCards(document); } catch (e) {}
    try { processProductPage(); } catch (e) {}
    try { bindVariantChanges(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-process when sections re-render (Theme Editor, some AJAX flows).
  document.addEventListener('shopify:section:load', function (e) {
    try { processCards((e && e.target) || document); } catch (err) {}
  });

  // Public hook for AJAX-loaded collection/search results:
  // call window.PromoDisplay.refresh(container) after injecting new cards.
  window.PromoDisplay = {
    refresh: function (root) { try { processCards(root || document); } catch (e) {} },
    config: config
  };
})();
